import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Calls as API, Customers , Company, TeamMembers } from '../services/api';
import { useAuth } from '../context/AuthContext';

const OUTCOMES = ['answered', 'not_answered', 'voicemail', 'callback_later'];
const OUT_COLOR = {
  answered: 'var(--green)', not_answered: 'var(--red)',
  voicemail: 'var(--yellow)', callback_later: 'var(--accent)',
};

const EMPTY_FORM = { customer_id: '', phone_number: '', call_type: 'outgoing',
  outcome: 'answered', duration_seconds: '', notes: '',
  call_at: new Date().toISOString().slice(0, 16),
  next_followup_date: '',
  capture_method: 'manual',      // 'manual' (typed by staff) or 'auto' (from a tracked call)
  provider_call_sid: '' };       // Exotel's call id, kept for reference/support — blank for manual entries

// Terminal states reported by the telephony provider — once we see one
// of these, the call is over and polling stops.
const TERMINAL_STATUSES = ['completed', 'failed', 'busy', 'no-answer'];
export default function Calls() {
  const { user }  = useAuth();
  const [list, setList]         = useState([]);
  const [custs, setCusts]       = useState([]);
  const [members, setMembers] = useState([]);
  const [company, setCompany] = useState(null);
  const [busy, setBusy]         = useState(true);
  const [showNew, setShowNew]   = useState(false);
  const [form, setForm]         = useState(EMPTY_FORM);
  const [saving, setSaving]     = useState(false);
  const [err, setErr]           = useState('');
  const [search, setSearch]     = useState('');
  // ── call tracking state ──
  // activeCall.sid is Exotel's own Call Sid — we never store our own
  // "in progress" row for this; the backend asks Exotel directly.
  const [activeCall, setActiveCall] = useState(null); // { sid, phone_number, status, duration_seconds }
  const [callStarting, setCallStarting] = useState(false);
  const pollRef = useRef(null);
  const load = useCallback(() => {
    setBusy(true);
    const callss = [API.list(), Customers.list(), Company.get()];
    // Only managers/finance need the team roster, for the history picker
    if (user.ryts == 1 || user.ryts == 3) callss.push(TeamMembers.list('y'));
    Promise.all(callss)
      .then(([calls, customers, corp, team]) => {
        setList(calls);
        setCusts(customers);
        setCompany(corp);
        if (team) setMembers(team);
      })
      .catch(console.error)
      .finally(() => setBusy(false));
  }, []);
  useEffect(load, [load]);

  // stop polling if the component unmounts mid-call
  useEffect(() => () => clearInterval(pollRef.current), []);

  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));

  const selectCustomer = e => {
    const id   = e.target.value;
    const cust = custs.find(c => String(c.id) === id);
    setForm(f => ({ ...f, customer_id: id, phone_number: cust?.number || f.phone_number }));
  };

  // ── start a tracked call ──────────────────────────────────────────
  const startTrackedCall = async (phoneNumber) => {
    if (!phoneNumber) { setErr('A phone number is needed to place the call'); return; }
    setErr('');
    setCallStarting(true);
    try {
      // Backend rings user.phone first, then bridges to phoneNumber via Exotel,
      // and returns Exotel's own call_sid — nothing is written to our DB yet.
      const res = await API.start({
        corp_id: user.corp_id,
        user_id: user.id,
        phone_number: phoneNumber,
      });
      setActiveCall({ sid: res.call_sid, phone_number: phoneNumber, status: 'ringing', duration_seconds: null });
      pollCallStatus(res.call_sid);
    } catch (ex) {
      setErr(ex.message || 'Could not start the call. Please try again.');
    } finally {
      setCallStarting(false);
    }
  };

  const pollCallStatus = (callSid) => {
    clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        // Thin proxy on the backend — asks Exotel's Call Details API for
        // this sid's live status/duration. Nothing read from our own DB.
        const status = await API.status(callSid);
        setActiveCall(prev => (prev && prev.sid === callSid) ? { ...prev, ...status } : prev);
        if (TERMINAL_STATUSES.includes(status.status)) {
          clearInterval(pollRef.current);
        }
      } catch (ex) {
        // transient network hiccup — keep polling, don't surface an error for every tick
      }
    }, 3000);
  };

  // once a call reaches a terminal state, auto-fill the form fields
  useEffect(() => {
    if (!activeCall || !TERMINAL_STATUSES.includes(activeCall.status)) return;

    const connected = activeCall.status === 'completed' && (activeCall.duration_seconds || 0) > 0;
    setForm(f => ({
      ...f,
      phone_number: activeCall.phone_number,
      call_type: 'outgoing',
      duration_seconds: activeCall.duration_seconds ?? 0,
      outcome: connected ? 'answered' : 'not_answered',
      capture_method: 'auto',
      provider_call_sid: activeCall.sid,
    }));

    // make sure the modal is open so staff can see the auto-filled result and save it
    setShowNew(true);
  }, [activeCall?.status]);

  const dismissActiveCall = () => {
    clearInterval(pollRef.current);
    setActiveCall(null);
  };
  
  const logCall = async () => {
    if (!form.customer_id || !form.phone_number) { setErr('Customer and phone number required'); return; }
    setSaving(true); setErr('');
    try {
      await API.log({ ...form, corp_id: user.corp_id, user_id: user.id, customer_id: +form.customer_id });
      setShowNew(false);
      setForm(EMPTY_FORM);
      setActiveCall(null);
      load();
    } catch (ex) { setErr(ex.message); }
    finally { setSaving(false); }
  };

  const custName = id => custs.find(c => c.id == id)?.company || `Customer ${id}`;
  const execName = id => members.find(m => m.id == id)?.name || `User ${id}`;
  const filtered = list.filter(c =>
    !search ||
    execName(c.user_id).toLowerCase().includes(search.toLowerCase()) ||
    (c.phone_number || '').includes(search)
  );
  console.log(members);
  
  const callStatusText = (status) => {
    switch (status) {
      case 'ringing': return 'Ringing your phone first — answer to connect the call…';
      case 'in-progress': return 'Connected — talking now. Duration is being tracked automatically.';
      case 'completed': return `Call ended — ${activeCall.duration_seconds ?? 0}s captured automatically.`;
      case 'failed': return "Call couldn't connect.";
      case 'busy': return 'Line was busy.';
      case 'no-answer': return "No one picked up.";
      default: return 'Calling…';
    }
  };

  return (
    <div className="page">
      <div className="page-header">
        <div className="page-title">📞 Call Log</div>
        <input className="search-bar" placeholder="Search customer, phone…"
          value={search} onChange={e => setSearch(e.target.value)} />
        <button className="btn btn-primary" onClick={() => { setShowNew(true); setErr(''); }}>+ Log Call</button>
      </div>

      {/* Live call banner — visible even if the modal is closed */}
      {activeCall && (
        <div className="alert" style={{
          background: TERMINAL_STATUSES.includes(activeCall.status) && activeCall.status !== 'completed'
            ? 'var(--red)' : 'var(--panel, #f1f5f9)',
          marginBottom: '.75rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        }}>
          <span>{activeCall.phone_number} — {callStatusText(activeCall.status)}</span>
          {TERMINAL_STATUSES.includes(activeCall.status) && (
            <button className="btn-icon" onClick={dismissActiveCall} title="Dismiss">✕</button>
          )}
        </div>
      )}

      {busy ? <div className="spinner-wrap"><div className="spinner" /></div> :
        filtered.length === 0 ? <div className="empty">No call logs yet</div> :
        filtered.map(c => (
          <div key={c.id} className="list-row" style={{ marginBottom: '.5rem' }}>
            <div className="row-main">
              <div className="row-title">
                {custName(c.customer_id)}
                {' · '}
                <button
                  type="button"
                  onClick={() => startTrackedCall(c.phone_number)}
                  disabled={!!activeCall && !TERMINAL_STATUSES.includes(activeCall.status)}
                  style={{
                    color: 'var(--accent)', background: 'none', border: 'none', padding: 0,
                    font: 'inherit', textDecoration: 'underline', cursor: 'pointer',
                  }}
                  title="Call this number and auto-track duration"
                >
                  📞 {c.phone_number}
                </button>
              </div>
              <div className="row-sub">
                {execName(c.user_id)} . {c.call_type}   · {c.call_at?.slice(0, 16)}
                {c.duration_seconds > 0 ? ` · ${c.duration_seconds}s` : ''}
              </div>
              {c.notes && <div style={{ color: 'var(--faint)', fontSize: '.78rem', marginTop: 2 }}>{c.notes}</div>}
              {c.next_followup_date && (
                <div style={{ fontSize: '.78rem', marginTop: 4, color: 'var(--accent)' }}>
                  📅 Follow-up logged for {c.next_followup_date}
                  <span style={{ color: 'var(--faint)', marginLeft: 6 }}>· track it on the Follow-ups page</span>
                </div>
              )}
            </div>
            <div className="row-actions">
              <span className="badge" style={{ background: OUT_COLOR[c.outcome] || 'var(--faint)', color: '#0f172a' }}>
                {c.outcome?.replace(/_/g, ' ')}
              </span>
              {c.capture_method === 'auto' && (
                <span className="badge" style={{ background: 'var(--faint)', color: '#0f172a', marginLeft: 6 }} title="Duration captured automatically via tracked call">
                  ✓ auto
                </span>
              )}
            </div>
          </div>
        ))
      }

      {/* Log Call Modal */}
      {showNew && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setShowNew(false)}>
          <div className="modal">
            <div className="modal-header">
              <span className="modal-title">Log Call</span>
              <button className="btn-icon" onClick={() => { setShowNew(false); }}>✕</button>
            </div>
            {err && <div className="alert alert-error">{err}</div>}
            <label style={{ marginBottom: '1rem' }}>
              Customer *
              <select value={form.customer_id} onChange={selectCustomer}>
                <option value="">-- Select Customer --</option>
                {custs.map(c => <option key={c.id} value={c.id}>{c.company} — {c.name}</option>)}
              </select>
            </label>
            <label style={{ marginBottom: '1rem' }}>
              Phone Number *
              <div style={{ display: 'flex', gap: 8 }}>
                <input type="tel" value={form.phone_number} onChange={set('phone_number')}
                  placeholder="+91 99999 99999" style={{ flex: 1 }} />
                <button
                  type="button"
                  className="btn btn-ghost"
                  onClick={() => startTrackedCall(form.phone_number)}
                  disabled={callStarting || !form.customer_id || !form.phone_number ||
                    (!!activeCall && !TERMINAL_STATUSES.includes(activeCall.status))}
                >
                  {callStarting ? 'Calling…' : '📞 Call Now'}
                </button>
              </div>
              <span style={{ fontSize: '.75rem', color: 'var(--faint)' }}>
                Calling here rings you first, then connects the customer — duration below fills in automatically.
              </span>
            </label>
            <div className="form-row" style={{ marginBottom: '1rem' }}>
              <label>
                Call Type
                <select value={form.call_type} onChange={set('call_type')}>
                  <option value="outgoing">Outgoing</option>
                  <option value="incoming">Incoming</option>
                  <option value="missed">Missed</option>
                </select>
              </label>
              <label>
                Outcome
                <select value={form.outcome} onChange={set('outcome')}>
                  {OUTCOMES.map(o => <option key={o} value={o}>{o.replace(/_/g, ' ')}</option>)}
                </select>
              </label>
            </div>
            <div className="form-row" style={{ marginBottom: '1rem' }}>
              <label>Date & Time<input type="datetime-local" value={form.call_at} onChange={set('call_at')} /></label>
              <label>
                Duration (seconds)
                <input type="number" value={form.duration_seconds} onChange={set('duration_seconds')} min="0" />
                {activeCall?.status === 'completed' && (
                  <span style={{ fontSize: '.72rem', color: 'var(--green)' }}>✓ auto-captured, edit if needed</span>
                )}
              </label>
            </div>
            <label style={{ marginBottom: '1rem' }}>
              Notes
              <textarea rows={2} value={form.notes} onChange={set('notes')} />
            </label>
            <label style={{ marginBottom: '1.2rem' }}>
              Next Follow-up Date <span style={{ color: 'var(--faint)', fontWeight: 400 }}>(optional)</span>
              <input type="date" value={form.next_followup_date} onChange={set('next_followup_date')} />
            </label>
            <div className="modal-footer">
              <button className="btn btn-primary" onClick={logCall} disabled={saving}>{saving ? 'Saving…' : 'Save Call'}</button>
              <button className="btn btn-ghost" onClick={() => setShowNew(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
